/**
 * Whitby Abrasives — custom JavaScript.
 *
 * Everything here is scoped to the bespoke wa-* pages (catalogue, wholesale,
 * influencer, about, home) or is a safe, site-wide progressive enhancement.
 * Loaded with `defer`; every feature is wrapped so a failure can never break
 * the store.
 *
 * Contents:
 *   1. Viewport / pinch-zoom accessibility fix
 *   2. Injected CSS (responsive bug-fixes, catalogue table mobile fix,
 *      contrast fix, hover/motion effects, sticky bars, print stylesheet)
 *   3. Scroll-reveal (wa- sections + catalogue product cards)
 *   4. Catalogue page enhancements (.wa-cat only):
 *        a. duplicate theme H1 removal
 *        b. live SKU / keyword filter
 *        c. sticky size-pill nav with scroll-spy
 *        d. sticky "Request a Quote" bar (replaces 28 repeated mailto CTAs)
 *        e. ItemList JSON-LD for search engines
 *   5. Back-to-top button (wa- pages)
 *
 * Warehouse theme hooks (for reference):
 *   document.addEventListener('variant:changed', e => e.detail.variant);
 *   document.addEventListener('product:added',   e => e.detail.variant / e.detail.quantity);
 *   document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
 */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------------
   * 1. Accessibility: re-enable pinch-zoom.
   * ------------------------------------------------------------------------- */
  try {
    var viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
    }
  } catch (e) {}

  /* ---------------------------------------------------------------------------
   * 2. Inject scoped CSS.
   * ------------------------------------------------------------------------- */
  var css = [
    /* --- Bug & mobile fixes (custom wa- pages) --- */
    '.wa-sub-nav{position:static !important;}',
    '.wa-container.wa-page-body{padding-left:24px;padding-right:24px;}',
    '.wa-form-group input,.wa-form-group select,.wa-form-group textarea{font-size:16px !important;}',
    '#influencer-apply{scroll-margin-top:90px;}',
    '.collection-drawer__close{min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;}',

    /* --- Fix: 100vw full-bleed pages cause a horizontal scrollbar on Windows
           (100vw includes the scrollbar width). Clip the overflow. --- */
    'html:has(.wa-cat),html:has(.wa-home),html:has(.wa-about){overflow-x:clip;}',

    /* --- Fix: duplicate H1 on catalogue pages (theme page title + custom H1) --- */
    'section[data-section-type="page"]:has(.wa-cat) .page__header{display:none;}',

    /* --- Fix: catalogue spec tables crushed on phones / narrow windows.
           Root cause: theme .rte{word-break:break-word} + 3-col table + the
           card keeping its 300px image column down to 600px. --- */
    '.wa-cat .wa-specs th,.wa-cat .wa-specs td{word-break:normal;overflow-wrap:normal;hyphens:none;}',
    '@media (max-width:880px){.wa-cat .wa-product{flex-direction:column;gap:14px;}.wa-cat .wa-product .wa-img{flex:0 0 auto;max-width:320px;width:100%;}}',
    '@media (max-width:560px){',
      '.wa-cat .wa-specs,.wa-cat .wa-specs tbody,.wa-cat .wa-specs tr,.wa-cat .wa-specs th,.wa-cat .wa-specs td{display:block;width:100%;box-sizing:border-box;}',
      '.wa-cat .wa-specs tr{border:0;border-bottom:1px solid #eee;padding:0 0 6px;}',
      '.wa-cat .wa-specs th.wa-group{background:#0047BA;color:#fff;border-radius:4px;margin:10px 0 4px;padding:6px 10px;width:100%;}',
      '.wa-cat .wa-specs th:not(.wa-group){border:0;padding:6px 10px 0;font-size:.74em;text-transform:uppercase;letter-spacing:.5px;color:#888;background:none;}',
      '.wa-cat .wa-specs td{border:0;padding:2px 10px 6px;}',
    '}',

    /* --- Fix: small orange labels failed WCAG AA contrast (3.0:1) --- */
    '.wa-cat .wa-feat-title,.wa-cat .wa-spec-title{color:#C45200 !important;}',

    /* --- Fancy: spec table row hover highlight --- */
    '.wa-cat .wa-specs tbody tr:hover th:not(.wa-group),.wa-cat .wa-specs tbody tr:hover td{background:#F2F6FE;}',

    /* --- Fancy: card hover lift --- */
    '.wa-card,.wa-oem-item,.wa-cat-card,.wa-info-box,.wa-inf-step,.wa-inf-product-card,.wa-inf-who-card{transition:transform .25s ease,box-shadow .25s ease;}',
    '.wa-card:hover,.wa-oem-item:hover,.wa-cat-card:hover,.wa-info-box:hover,.wa-inf-step:hover,.wa-inf-product-card:hover,.wa-inf-who-card:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,.10);}',
    '.wa-cat .wa-product{transition:transform .25s ease,box-shadow .25s ease;}',
    '.wa-cat .wa-product:hover{transform:translateY(-3px);box-shadow:0 10px 28px rgba(0,0,0,.09);}',

    /* --- Fancy: scroll reveal --- */
    '.wa-reveal{opacity:0;transform:translateY(28px);transition:opacity .6s ease,transform .6s ease;will-change:opacity,transform;}',
    '.wa-reveal.is-visible{opacity:1;transform:none;}',

    /* --- Catalogue: live filter box --- */
    '.wa-filter-wrap{max-width:1200px;margin:0 auto 20px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;}',
    '.wa-filter-wrap input{flex:1 1 260px;font-size:16px;padding:12px 16px;border:2px solid #cfd6e2;border-radius:8px;font-family:inherit;}',
    '.wa-filter-wrap input:focus{outline:none;border-color:#0047BA;}',
    '.wa-filter-count{font-size:.9em;font-weight:600;color:#565B62;white-space:nowrap;}',

    /* --- Catalogue: sticky size-pill nav with scroll-spy --- */
    '.wa-toc-sticky{position:fixed;top:0;left:0;right:0;z-index:980;background:rgba(255,255,255,.96);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);box-shadow:0 2px 12px rgba(0,0,0,.10);padding:8px 14px;display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;transform:translateY(-110%);transition:transform .25s ease;}',
    '.wa-toc-sticky::-webkit-scrollbar{display:none;}',
    '.wa-toc-sticky.is-shown{transform:none;}',
    '.wa-toc-sticky a{flex:none;font-size:.82em;font-weight:700;color:#0047BA;background:#fff;border:1px solid #cfd6e2;border-radius:999px;padding:5px 13px;text-decoration:none;white-space:nowrap;}',
    '.wa-toc-sticky a.is-now,.wa-toc-sticky a:hover{background:#0047BA;color:#fff;border-color:#0047BA;}',

    /* --- Catalogue: sticky quote bar --- */
    '.wa-qb-bar{position:fixed;left:0;right:0;bottom:0;z-index:990;background:#0047BA;color:#fff;display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:10px 18px;padding:10px 44px 10px 16px;box-shadow:0 -4px 18px rgba(0,0,0,.18);transform:translateY(110%);transition:transform .3s ease;}',
    '.wa-qb-bar.is-shown{transform:none;}',
    '.wa-qb-bar .wa-qb-txt{font-weight:700;font-size:.95em;}',
    '.wa-qb-bar a{display:inline-block;background:#ff6b00;color:#fff;font-weight:700;font-size:.9em;padding:9px 20px;border-radius:6px;text-decoration:none;text-transform:uppercase;letter-spacing:.4px;}',
    '.wa-qb-bar a.wa-qb-tel{background:#fff;color:#0047BA;}',
    '.wa-qb-bar button{position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:0;color:rgba(255,255,255,.8);font-size:22px;line-height:1;cursor:pointer;padding:6px;}',
    '.wa-qb-bar button:hover{color:#fff;}',
    'body.wa-qb-active .wa-cat .wa-cta a.wa-quote-btn.ghost{display:none;}',
    'body.wa-qb-active{padding-bottom:64px;}',

    /* --- Back-to-top button --- */
    '.wa-backtop{position:fixed;right:18px;bottom:84px;z-index:985;width:46px;height:46px;border-radius:50%;background:#fff;color:#0047BA;border:1px solid #cfd6e2;box-shadow:0 4px 14px rgba(0,0,0,.16);cursor:pointer;font-size:20px;line-height:1;opacity:0;visibility:hidden;transform:translateY(8px);transition:opacity .25s ease,transform .25s ease,visibility .25s;}',
    '.wa-backtop.is-shown{opacity:1;visibility:visible;transform:none;}',
    '.wa-backtop:hover{background:#0047BA;color:#fff;}',

    /* --- Print stylesheet: catalogue pages print clean for purchasing --- */
    '@media print{',
      '.wa-qb-bar,.wa-backtop,.wa-toc-sticky,.wa-filter-wrap,.wa-cat .wa-top-cta,.wa-cat .wa-cta,.wa-cat .wa-toc{display:none !important;}',
      '.wa-cat .wa-product{break-inside:avoid;box-shadow:none !important;border:1px solid #bbb;}',
      '.wa-cat .wa-product .wa-img{max-width:140px;}',
      '.wa-cat .wa-specs th,.wa-cat .wa-specs td{border:1px solid #999 !important;}',
    '}',

    /* --- Respect reduced-motion preference --- */
    '@media (prefers-reduced-motion:reduce){.wa-reveal{opacity:1 !important;transform:none !important;transition:none !important;}.wa-card,.wa-oem-item,.wa-cat-card,.wa-info-box,.wa-inf-step,.wa-inf-product-card,.wa-inf-who-card,.wa-cat .wa-product{transition:none !important;}.wa-toc-sticky,.wa-qb-bar,.wa-backtop{transition:none !important;}}'
  ].join('');

  try {
    var styleEl = document.createElement('style');
    styleEl.id = 'wa-custom-enhancements';
    styleEl.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(styleEl);
  } catch (e) {}

  /* ---------------------------------------------------------------------------
   * 3. Scroll-reveal — wa- sections and catalogue product cards.
   * ------------------------------------------------------------------------- */
  function initScrollReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll(
      '.wa-content-section, .wa-section, .wa-inf-section, .wa-cta-bar, .wa-cat .wa-product'
    );
    if (!targets.length) return;

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });

    targets.forEach(function (el) {
      el.classList.add('wa-reveal');
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------------
   * 4. Catalogue page enhancements — only run when .wa-cat exists.
   * ------------------------------------------------------------------------- */
  function initCatalogue() {
    var cat = document.querySelector('.wa-cat');
    if (!cat) return;

    /* 4a. Remove the theme's duplicate page-title H1 (fallback for browsers
           without :has() support — the CSS rule above handles the rest). */
    try {
      var section = cat.closest('section[data-section-type="page"]');
      var hdr = section && section.querySelector('.page__header');
      if (hdr) hdr.style.display = 'none';
    } catch (e) {}

    /* 4b. Live SKU / keyword filter. */
    try {
      var products = [].slice.call(cat.querySelectorAll('.wa-product'));
      var toc = cat.querySelector('.wa-toc');
      if (products.length > 3) {
        var wrap = document.createElement('div');
        wrap.className = 'wa-filter-wrap';
        var input = document.createElement('input');
        input.type = 'search';
        input.placeholder = 'Filter by SKU, size or keyword — e.g. C45, 5", stainless';
        input.setAttribute('aria-label', 'Filter catalogue products');
        var count = document.createElement('span');
        count.className = 'wa-filter-count';
        count.textContent = products.length + ' products';
        wrap.appendChild(input);
        wrap.appendChild(count);
        if (toc && toc.parentNode) toc.parentNode.insertBefore(wrap, toc.nextSibling);
        else cat.insertBefore(wrap, cat.firstChild);

        var chrome = [].slice.call(cat.querySelectorAll('.wa-size-divider, .wa-series, .wa-callout'));
        var texts = products.map(function (p) { return p.textContent.toLowerCase(); });

        input.addEventListener('input', function () {
          var q = input.value.trim().toLowerCase();
          var shown = 0;
          products.forEach(function (p, i) {
            var ok = !q || texts[i].indexOf(q) > -1;
            p.style.display = ok ? '' : 'none';
            if (ok) shown++;
          });
          chrome.forEach(function (el) { el.style.display = q ? 'none' : ''; });
          count.textContent = q ? (shown + ' of ' + products.length + ' products') : (products.length + ' products');
        });
      }
    } catch (e) {}

    /* 4c. Sticky size-pill nav with scroll-spy. */
    try {
      var srcToc = cat.querySelector('.wa-toc');
      var pills = srcToc ? [].slice.call(srcToc.querySelectorAll('a.wa-toc-pill, a.wa-toc-cat')) : [];
      var anchored = pills.filter(function (a) {
        var h = a.getAttribute('href') || '';
        return h.charAt(0) === '#' && document.getElementById(h.slice(1));
      });
      if (anchored.length > 1 && 'IntersectionObserver' in window) {
        var bar = document.createElement('nav');
        bar.className = 'wa-toc-sticky';
        bar.setAttribute('aria-label', 'Catalogue sections');
        anchored.forEach(function (a) {
          var c = document.createElement('a');
          c.href = a.getAttribute('href');
          c.textContent = a.textContent.trim();
          bar.appendChild(c);
        });
        document.body.appendChild(bar);

        var barLinks = [].slice.call(bar.querySelectorAll('a'));
        var sections = anchored.map(function (a) { return document.getElementById(a.getAttribute('href').slice(1)); });

        var spy = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var id = '#' + entry.target.id;
            barLinks.forEach(function (l) {
              l.classList.toggle('is-now', l.getAttribute('href') === id);
            });
          });
        }, { rootMargin: '-15% 0px -70% 0px' });
        sections.forEach(function (s) { spy.observe(s); });

        var tocBottom = function () {
          var r = srcToc.getBoundingClientRect();
          return r.bottom + window.pageYOffset;
        };
        var onScroll = function () {
          bar.classList.toggle('is-shown', window.pageYOffset > tocBottom());
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    } catch (e) {}

    /* 4d. Sticky "Request a Quote" bar — replaces the repeated mailto CTAs
           (the per-product "Add to Cart" links stay). Dismissible per session. */
    try {
      if (!sessionStorage.getItem('waQbDismissed')) {
        var qb = document.createElement('div');
        qb.className = 'wa-qb-bar';
        qb.innerHTML =
          '<span class="wa-qb-txt">Buying in volume?</span>' +
          '<a href="/pages/get-a-quote">Request a Bulk Quote</a>' +
          '<a class="wa-qb-tel" href="tel:9054301777">Call (905) 430-1777</a>' +
          '<button type="button" aria-label="Dismiss quote bar">&times;</button>';
        document.body.appendChild(qb);
        document.body.classList.add('wa-qb-active');

        qb.querySelector('button').addEventListener('click', function () {
          qb.remove();
          document.body.classList.remove('wa-qb-active');
          try { sessionStorage.setItem('waQbDismissed', '1'); } catch (e2) {}
        });

        var qbScroll = function () {
          qb.classList.toggle('is-shown', window.pageYOffset > 600);
        };
        window.addEventListener('scroll', qbScroll, { passive: true });
        qbScroll();
      }
    } catch (e) {}

    /* 4e. ItemList JSON-LD so search engines understand the catalogue. */
    try {
      var items = [];
      [].slice.call(cat.querySelectorAll('.wa-product')).forEach(function (p, i) {
        var h2 = p.querySelector('h2');
        var sku = p.querySelector('.wa-sku-badge');
        var link = p.querySelector('a[href^="/products/"]');
        if (!h2) return;
        var item = {
          '@type': 'ListItem',
          'position': i + 1,
          'name': h2.textContent.trim()
        };
        if (link) item.url = location.origin + link.getAttribute('href');
        if (sku) item.item = { '@type': 'Product', 'name': h2.textContent.trim(), 'sku': sku.textContent.replace(/^SKU\s*/i, '').trim() };
        items.push(item);
      });
      if (items.length) {
        var ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          'name': document.title,
          'numberOfItems': items.length,
          'itemListElement': items
        });
        document.head.appendChild(ld);
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 5. Mobile navigation keyboard and accessible-name support.
   * ------------------------------------------------------------------------- */
  function getVisibleFocusable(root) {
    if (!root) return [];
    return [].slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      var style = getComputedStyle(el);
      if (el.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden') return false;
      var rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      var visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
      var visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      return visibleWidth >= Math.min(24, rect.width) && visibleHeight >= Math.min(24, rect.height);
    });
  }

  function trapTabKey(event, root) {
    if (event.key !== 'Tab') return;
    var focusable = getVisibleFocusable(root);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initMobileNavigationAccessibility() {
    try {
      var toggle = document.querySelector('.header__mobile-nav-toggle[aria-controls="mobile-menu"]');
      var menu = document.getElementById('mobile-menu');
      if (!toggle || !menu) return;

      var openLabel = toggle.getAttribute('data-open-label') || toggle.getAttribute('aria-label') || 'Open menu';
      var closeLabel = toggle.getAttribute('data-close-label') || 'Close menu';

      function isOpen() {
        return toggle.getAttribute('aria-expanded') === 'true';
      }

      function syncLabel() {
        toggle.setAttribute('aria-label', isOpen() ? closeLabel : openLabel);
      }

      function focusFirstMenuItem() {
        window.setTimeout(function () {
          if (!isOpen()) return;
          var firstItem = getVisibleFocusable(menu)[0];
          if (firstItem) firstItem.focus();
        }, 50);
      }

      function syncMenuState() {
        syncLabel();
        if (isOpen()) focusFirstMenuItem();
      }

      toggle.addEventListener('click', function () {
        window.setTimeout(syncMenuState, 0);
      });

      document.addEventListener('keydown', function (event) {
        if (!isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          toggle.click();
          window.setTimeout(function () { toggle.focus(); }, 0);
          return;
        }
        trapTabKey(event, toggle.parentNode);
      });

      if ('MutationObserver' in window) {
        new MutationObserver(syncMenuState).observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
      }
      syncLabel();
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 6. Mobile search focus, Escape and focus-restoration support.
   * ------------------------------------------------------------------------- */
  function initMobileSearchAccessibility() {
    try {
      var trigger = document.querySelector('[data-action="toggle-search"]');
      var wrapper = document.querySelector('.header__search-bar-wrapper');
      var header = document.querySelector('.header');
      if (!trigger || !wrapper || !header) return;

      var input = wrapper.querySelector('.search-bar__input');
      var closeButton = wrapper.querySelector('[data-action="unfix-search"]');
      var openLabel = trigger.getAttribute('data-open-label') || trigger.getAttribute('aria-label') || 'Open search';
      var closeLabel = trigger.getAttribute('data-close-label') || 'Close search';

      function isOpen() {
        return header.classList.contains('header--search-expanded') && wrapper.classList.contains('is-visible');
      }

      function syncTrigger() {
        var open = isOpen();
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        trigger.setAttribute('aria-label', open ? closeLabel : openLabel);
      }

      var searchWasOpen = isOpen();

      function focusSearchInput() {
        window.setTimeout(function () {
          if (isOpen() && input) input.focus();
        }, 50);
      }

      function syncSearchState() {
        var open = isOpen();
        syncTrigger();
        if (open && !searchWasOpen) focusSearchInput();
        searchWasOpen = open;
      }

      trigger.addEventListener('click', function () {
        window.setTimeout(syncSearchState, 0);
      });

      if (closeButton) {
        closeButton.addEventListener('click', function () {
          window.setTimeout(function () {
            if (isOpen()) trigger.click();
            window.setTimeout(function () { trigger.focus(); }, 0);
          }, 0);
        });
      }

      wrapper.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || !isOpen()) return;
        event.preventDefault();
        if (closeButton && wrapper.classList.contains('is-fixed')) closeButton.click();
        else trigger.click();
        window.setTimeout(function () { trigger.focus(); }, 0);
      });

      if ('MutationObserver' in window) {
        new MutationObserver(syncSearchState).observe(header, { attributes: true, attributeFilter: ['class'] });
      }
      syncTrigger();
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 7. Collection-filter drawer focus and Escape support.
   * ------------------------------------------------------------------------- */
  function initCollectionDrawerAccessibility() {
    try {
      var drawer = document.getElementById('mobile-collection-filters');
      if (!drawer) return;

      function opener() {
        return document.querySelector('[aria-controls="mobile-collection-filters"][data-action="open-drawer"]');
      }

      function closeButton() {
        return drawer.querySelector('[data-action="close-drawer"]');
      }

      function isOpen() {
        return drawer.getAttribute('aria-hidden') === 'false';
      }

      var drawerWasOpen = isOpen();

      function syncDrawerState() {
        var open = isOpen();
        if (open && !drawerWasOpen) {
          window.setTimeout(function () {
            var close = closeButton();
            if (isOpen() && close) close.focus();
          }, 50);
        } else if (!open && drawerWasOpen) {
          window.setTimeout(function () {
            var openControl = opener();
            if (openControl) openControl.focus();
          }, 0);
        }
        drawerWasOpen = open;
      }

      document.addEventListener('click', function (event) {
        var openControl = event.target.closest && event.target.closest('[aria-controls="mobile-collection-filters"][data-action="open-drawer"]');
        if (openControl) {
          window.setTimeout(function () {
            var close = closeButton();
            if (isOpen() && close) close.focus();
          }, 0);
          return;
        }

        var closeControl = event.target.closest && event.target.closest('#mobile-collection-filters [data-action="close-drawer"]');
        if (closeControl) {
          window.setTimeout(function () {
            var open = opener();
            if (open) open.focus();
          }, 0);
        }
      });

      document.addEventListener('keydown', function (event) {
        if (!isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          var close = closeButton();
          if (close) close.click();
          window.setTimeout(function () {
            var open = opener();
            if (open) open.focus();
          }, 0);
          return;
        }
        if (event.key === 'Tab' && !drawer.contains(document.activeElement)) {
          var first = getVisibleFocusable(drawer)[0];
          if (first) {
            event.preventDefault();
            first.focus();
          }
          return;
        }
        trapTabKey(event, drawer);
      });

      if ('MutationObserver' in window) {
        new MutationObserver(syncDrawerState).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 8. Restore native required-field validation on the custom contact form.
   * ------------------------------------------------------------------------- */
  function initContactValidation() {
    try {
      var form = document.querySelector('.wa-contact form[action*="/contact"]');
      if (!form || !form.querySelector('[required]')) return;
      form.removeAttribute('novalidate');
      form.noValidate = false;
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 9. Keyboard access and focus restoration for product image zoom.
   * ------------------------------------------------------------------------- */
  function initProductGalleryAccessibility() {
    try {
      var imageItemSelector = '.product-gallery__carousel-item[data-media-type="image"]';
      var galleries = [].slice.call(document.querySelectorAll('.product-gallery'));

      galleries.forEach(function (gallery) {
        var viewer = gallery.querySelector('.pswp');
        if (!viewer || !gallery.querySelector('.product-gallery__carousel--zoomable')) return;
        var zoomNotice = gallery.querySelector('.product-gallery__zoom-notice');
        var zoomLabel = zoomNotice ? zoomNotice.textContent.trim() : 'Zoom image';
        var lastTrigger = null;
        var viewerWasOpen = false;

        function isZoomableImageItem(item) {
          return !!(item && item.matches(imageItemSelector) && item.querySelector('img[data-zoom]'));
        }

        function zoomableImageItems() {
          return [].slice.call(gallery.querySelectorAll(imageItemSelector)).filter(isZoomableImageItem);
        }

        function updateItems() {
          [].slice.call(gallery.querySelectorAll('[data-wa-zoom-accessible]')).forEach(function (item) {
            if (isZoomableImageItem(item)) return;
            item.removeAttribute('data-wa-zoom-accessible');
            item.removeAttribute('role');
            item.removeAttribute('tabindex');
            item.removeAttribute('aria-label');
          });

          zoomableImageItems().forEach(function (item) {
            var image = item.querySelector('img[data-zoom]');
            item.setAttribute('data-wa-zoom-accessible', 'true');
            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', item.classList.contains('is-selected') ? '0' : '-1');
            item.setAttribute('aria-label', zoomLabel + (image && image.alt ? ': ' + image.alt : ''));
          });
        }

        function syncViewer() {
          if (!viewer) return;
          var open = viewer.classList.contains('pswp--open') && viewer.getAttribute('aria-hidden') !== 'true';
          if (open) {
            viewer.setAttribute('role', 'dialog');
            viewer.setAttribute('aria-modal', 'true');
            if (!viewer.getAttribute('aria-label')) viewer.setAttribute('aria-label', zoomLabel);
          } else if (viewerWasOpen && lastTrigger) {
            updateItems();
            lastTrigger.focus();
          }
          viewerWasOpen = open;
        }

        gallery.addEventListener('keydown', function (event) {
          var item = event.target.closest && event.target.closest(imageItemSelector);
          if (!isZoomableImageItem(item) || event.target !== item || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          lastTrigger = item;
          item.querySelector('img[data-zoom]').click();
          window.setTimeout(syncViewer, 0);
        });

        gallery.addEventListener('click', function (event) {
          var item = event.target.closest && event.target.closest(imageItemSelector);
          var interactive = event.target.closest && event.target.closest('a,button,input,select,textarea,video,model-viewer,[role="button"]');
          if (!isZoomableImageItem(item) || (interactive && interactive !== item)) return;
          lastTrigger = item;
          window.setTimeout(syncViewer, 0);
        });

        if ('MutationObserver' in window) {
          new MutationObserver(updateItems).observe(gallery, { attributes: true, subtree: true, attributeFilter: ['class'] });
          if (viewer) {
            new MutationObserver(syncViewer).observe(viewer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
          }
        }
        updateItems();
        syncViewer();
      });
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------------
   * 10. Back-to-top button — long wa- pages only.
   * ------------------------------------------------------------------------- */
  function initBackTop() {
    try {
      if (!document.querySelector('.wa-cat, .wa-home, .wa-about, .wa-content-section, .wa-inf-section')) return;
      var btn = document.createElement('button');
      btn.className = 'wa-backtop';
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Back to top');
      btn.innerHTML = '&uarr;';
      document.body.appendChild(btn);
      btn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      var onScroll = function () {
        btn.classList.toggle('is-shown', window.pageYOffset > 900);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } catch (e) {}
  }

  function initAll() {
    initScrollReveal();
    initCatalogue();
    initMobileNavigationAccessibility();
    initMobileSearchAccessibility();
    initCollectionDrawerAccessibility();
    initContactValidation();
    initProductGalleryAccessibility();
    initBackTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
